import { useLocation, useNavigate } from 'react-router-dom'
import CustomCardHeader from '../../../components/card/CustomCardHeader'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import CustomCard from '../../../../src/components/card/CustomCard'
import { useDispatch, useSelector } from 'react-redux'
import CustomTable, {
  TableCell,
  TableHeader,
  TableRow,
} from '../../../components/table/CustomTable'
import CardBody from '../../../components/card/CardBody'
import ReactPaginate from 'react-paginate'
import Status, { Tag } from '../../../components/table/Status'
import { debounce } from 'lodash'
import { capitalizeFirstLetter } from '../../../helpers/stringHelper'
import CustomTableItemLoader from '../../../components/CustomTableItemLoader'
import NavbarInput from '../../../components/form/NavbarInput'
import LabeledSelect from 'components/selects/LabeledSelect'
import FormLabel from 'components/form/FormLabel'
import { getCartReportListing } from 'redux/slices/cartReportingSlice'
import { setPage } from 'redux/slices/cartReportingSlice'
import Pagination from 'components/Pagination'

const CartlistTable = () => {
  const currentPath = '/cart-list'
  const sortOptions = [
    { label: 'Count: Ascending', value: 'ASC' },
    { label: 'Count: Descending', value: 'DESC' },
  ]

  const [selectedOption, setSelectedOption] = useState(sortOptions[1])

  const dispatch = useDispatch()
  const cart = useSelector((state) => state.cartList)
  const [cartListItem, setCartListItem] = useState('')
  const page = cart.page

  const firstRenderUpdate = useRef(true)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchPlaceholder, setSearchPlaceholder] = useState('')

  const handleSortOptionSelect = (selected, reset = false) => {
    if (selected) {
      setSelectedOption(selected)
      getCartItems({
        page: reset ? 1 : page,
        item: cartListItem,
        sort: selected.value,
      })
    }
  }
  function dispatchCartlist() {
    const paramData = {
      item: cartListItem,
      sort: selectedOption.value,
    }
    dispatch(getCartReportListing(paramData))
  }

  const route = useCallback((page, query = '', selectedOpt) => {
    navigate(
      `${currentPath}?query=${query}&order=${selectedOpt}&page=${page ?? 1}`,
    )
  }, [])

  const getCartItems = (payload) => {
    dispatch(getCartReportListing(payload))
    route(payload.page, payload.item, payload.sort)
  }

  useEffect(() => {
    if (firstRenderUpdate.current) {
      firstRenderUpdate.current = false
      const urlParam = new URLSearchParams(location.search)
      const { pathname } = location
      const page = pathname === currentPath ? urlParam.get('page') ?? 1 : 1
      const cartlistName =
        pathname === currentPath
          ? typeof urlParam.get('query') === 'object'
            ? ''
            : urlParam.get('query')
          : ''
      setCartListItem(cartlistName)
      setSearchPlaceholder(cartlistName)
      dispatch(setPage(page))
      getCartItems({
        page,
        item: cartlistName,
        sort: selectedOption.value,
      })
    }
  }, [])

  const debouncedSearchCartlistInput = useCallback(
    debounce((e) => {
      setCartListItem(e.target.value)
      getCartItems({
        page: 1,
        item: e.target.value,
        sort: selectedOption.value,
      })
    }, 1000),
    [],
  )

  const onPageClick = (data) => {
    const currentPage = data.selected + 1
    getCartItems({
      page: currentPage,
      item: cartListItem,
      sort: selectedOption.value,
    })
  }

  return (
    <CustomCard>
      <CustomCardHeader contentPosition="none">
        <div className="w-full flex items-center justify-between">
          <h2 className="text-white text-2xl">Cart Reporting</h2>
        </div>
      </CustomCardHeader>
      <CardBody>
        <div className="flex items-end justify-between">
          <div className="flex flex-col">
            <FormLabel label="Sort by" />
            <div className="w-60">
              <LabeledSelect
                options={sortOptions}
                value={selectedOption || ''}
                onChange={(selected) => handleSortOptionSelect(selected, true)}
              />
            </div>
          </div>

          <div className=" mb-5 border border-gray-300 rounded">
            <NavbarInput
              value={searchPlaceholder}
              placeholder="Search cart item"
              onChange={(e) => {
                dispatch(setPage(1))
                debouncedSearchCartlistInput(e)
                setSearchPlaceholder(e.target.value)
              }}
            />
          </div>
        </div>

        <CustomTable>
          <thead>
            <TableRow>
              <TableHeader>Product</TableHeader>
              <TableHeader>Manufacturing Company</TableHeader>
              <TableHeader className="text-right">Price</TableHeader>
              <TableHeader className="text-right">Discount</TableHeader>
              <TableHeader>Availability</TableHeader>
              <TableHeader className="text-right">Cartlist Count</TableHeader>
            </TableRow>
          </thead>
          {cart.loading || cart.error.length > 0 || cart.data?.length === 0 ? (
            <CustomTableItemLoader
              loaderItemHeight="h-[48px]"
              loading={cart.loading}
              error={cart.error}
              isEmpty={cart?.data.length === 0}
              count={10}
              colSpan={6}
              retry={dispatchCartlist}
            />
          ) : (
            <tbody>
              {cart.data.map((cartList, index) => (
                <TableRow key={cartList?.product_id}>
                  <TableCell className="font-semibold">
                    {cartList?.label}
                    {(() => {
                      if (cartList?.has_variants === true) {
                        return (
                          <div className="font-light text-xs">
                            Variant:{' '}
                            {Object.values(cartList?.variant_values ?? {}).map(
                              (val, index) => (
                                <span key={index}>
                                  {capitalizeFirstLetter(val)}
                                  {index ===
                                    Object.keys(cartList?.variant_values).length -
                                    1
                                    ? ''
                                    : ', '}
                                </span>
                              ),
                            )}
                          </div>
                        )
                      }
                    })()}
                  </TableCell>
                  <TableCell>
                    {cartList?.manufacturing_company ? (
                      <Tag label={cartList?.manufacturing_company} />
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    NPR. {cartList?.price}
                  </TableCell>
                  <TableCell className="text-right">
                    {cartList?.discount ? `${cartList?.discount}` : 0}%
                  </TableCell>
                  <TableCell>
                    <Status
                      label={
                        cartList?.disabled
                          ? 'Archived'
                          : cartList?.sold_out
                            ? 'Out of Stock'
                            : 'In Stock'
                      }
                      isFailure={cartList?.sold_out || cartList?.disabled}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {cartList?.count ?? 0}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          )}
        </CustomTable>

        {cart.error.length > 0 || cart.data?.length === 0 || cart.loading ? (
          <span />
        ) : (
          <Pagination
            pageCount={cart.total_pages}
            onPageChange={onPageClick}
            forcePage={page - 1}
          />        )}
      </CardBody>
    </CustomCard>
  )
}

export default CartlistTable
